import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import StepWizard from "react-step-wizard";
import "../../CSS/general.css";
import Step1SelectJobs from "../SuppressionSteps/Step1SelectJobs";
import Step2Duration from "../SuppressionSteps/Step2Duration";
import ConfirmOverwriteModal from "./ConfirmOverwriteModal";
import {
    DURATION_MODE,
    resolveExpiry,
    isDurationValid,
    validateNote,
} from "../../Helpers/SuppressionDuration";

/**
 * Task 9 — the suppress flow: pick jobs, then pick a duration.
 *
 * Structure follows components/MainComponents/JobControlPopup.js, the existing
 * "select items then schedule" wizard: a react-step-wizard instance, a horizontal stepper header
 * using the .horizontal-stepper / .h-step classes from CSS/general.css, and the shared
 * components/JobControl/ActionButton.js for Next / Previous / Finish / Cancel.
 *
 * Nothing is written until Finish. Closing the dialog any other way leaves the error exactly as
 * it was, which the specification calls out explicitly.
 */

const EMPTY_DURATION = {
    mode: DURATION_MODE.PRESET,
    presetDays: 30,
    customDate: null,
};

export default function SuppressErrorPopup({
    show,
    knownError,
    jobs,
    /** Pre-selected jobs, duration and note, used when amending an existing suppression. */
    initialJobIds,
    initialDuration,
    initialNote,
    /** When true the job step is skipped — amending applies to one existing rule. */
    jobStepDisabled,
    /** C4 — [{ jobId, suppressedUntil }] already suppressed for this known error. */
    alreadySuppressed,
    onConfirm,
    onClose,
}) {
    const [wizard, setWizard] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [selectedJobIds, setSelectedJobIds] = useState([]);
    const [duration, setDuration] = useState(EMPTY_DURATION);
    const [note, setNote] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingOverwrites, setPendingOverwrites] = useState(null);

    // Reset on every open so a previous, abandoned attempt never leaks into the next one — the note
    // especially, since a justification written for one error must never be attached to another.
    //
    // Amending seeds the note from the existing rule (initialNote). Without that the field would
    // open blank and the customer's saved justification would be silently replaced with nothing the
    // moment they changed an expiry — a destructive side effect of an unrelated edit.
    useEffect(() => {
        if (!show) return;
        setSelectedJobIds(initialJobIds || []);
        setDuration(initialDuration || EMPTY_DURATION);
        setNote(initialNote || "");
        setActiveStep(0);
        setIsSubmitting(false);
        setPendingOverwrites(null);
        if (wizard) wizard.goToStep(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, knownError]);

    const handleClose = () => {
        // Cancel changes nothing — the parent is only told about a confirmed choice.
        onClose();
    };

    /** Actually sends the choice. Called either directly or after the overwrite prompt is accepted. */
    const submit = async () => {
        setIsSubmitting(true);
        try {
            await onConfirm({
                jobIds: selectedJobIds,
                // C1 — one duration for the whole action, applied to every selected job.
                suppressedUntil: resolveExpiry(duration),
                note: (note || "").trim() || null,
                duration,
            });
        } finally {
            setIsSubmitting(false);
            setPendingOverwrites(null);
        }
    };

    /**
     * C4 — if any selected job already has this error suppressed, ask before replacing its expiry.
     * Amending is allowed, but it must be a decision rather than a side effect of re-suppressing.
     */
    const handleFinish = async () => {
        const existing = new Map((alreadySuppressed || []).map((s) => [s.jobId, s.suppressedUntil]));
        const overwrites = selectedJobIds
            .filter((jobId) => existing.has(jobId))
            .map((jobId) => ({
                jobId,
                jobName: (jobs || []).find((j) => j.jobId === jobId)?.jobName || jobId,
                currentExpiry: existing.get(jobId),
            }));

        if (overwrites.length > 0) {
            setPendingOverwrites(overwrites);
            return;
        }
        await submit();
    };

    const steps = ["Select jobs", "Duration"];

    // The footer lives here rather than inside each step, so these gates are answered by the
    // component that already owns the state both steps mutate. Step 1 previously validated on
    // click and showed an inline error; Next is simply disabled until a job is chosen.
    const step1Ready = selectedJobIds.length > 0;
    const step2Ready = isDurationValid(duration) && validateNote(note).valid;
    // Amending renders the duration step alone, so it is always the final step.
    const onLastStep = jobStepDisabled || activeStep === steps.length - 1;

    return (
        <Modal isOpen={show} toggle={handleClose} size="lg" backdrop="static"
               className="suppression-modal">
            <ModalHeader>
                <span style={{ fontSize: 20 }}>
                    {jobStepDisabled ? "Amend suppression" : "Suppress"}
                    {knownError ? ` — ${knownError.errorId}` : ""}
                </span>
                <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    style={{ transform: "scale(0.75)", position: "absolute", right: 20, top: 22 }}
                    onClick={handleClose}
                />
            </ModalHeader>

            <ModalBody>
                {knownError && (
                    <p className="text-muted">
                        {knownError.name}
                    </p>
                )}

                {!jobStepDisabled && (
                    <div className="horizontal-stepper mb-3">
                        {steps.map((label, i) => (
                            <div
                                key={label}
                                className={`h-step ${
                                    activeStep === i ? " active" : activeStep > i ? " completed" : ""
                                }`}
                            >
                                <div className="circle">
                                    <span>{i + 1}</span>
                                </div>
                                <div className="content">{label}</div>
                                {i < steps.length - 1 && <div className="line" />}
                            </div>
                        ))}
                    </div>
                )}

                {jobStepDisabled ? (
                    // Amending an existing rule: only the duration is in play, so there is no
                    // wizard to run — render the duration step on its own. It needs none of the
                    // props react-step-wizard would inject now that the footer lives below.
                    <Step2Duration
                        duration={duration}
                        onChange={setDuration}
                        note={note}
                        onNoteChange={setNote}
                        selectedJobCount={selectedJobIds.length}
                    />
                ) : (
                    <StepWizard
                        instance={setWizard}
                        onStepChange={(e) => setActiveStep(e.activeStep - 1)}
                        // Disables react-step-wizard's default fade/slide between steps (~0.8s).
                        // getTransitions() returns this object as-is when truthy, so an empty one
                        // leaves enterRight/enterLeft/exitRight/exitLeft unset and the step change
                        // is instant. Step's own defaultProps turn the resulting undefined into "",
                        // so no stray class reaches the wrapper's className.
                        transitions={{}}
                    >
                        <Step1SelectJobs
                            jobs={jobs}
                            selectedJobIds={selectedJobIds}
                            onSelectionChanged={setSelectedJobIds}
                            alreadySuppressed={alreadySuppressed}
                        />
                        <Step2Duration
                            duration={duration}
                            onChange={setDuration}
                            note={note}
                            onNoteChange={setNote}
                            selectedJobCount={selectedJobIds.length}
                        />
                    </StepWizard>
                )}
            </ModalBody>

            {/*
              The actions live in a ModalFooter, matching this feature's other dialogs
              (ConfirmOverwriteModal, ConfirmSuppressionRemovalModal, KnownErrorDetailsModal).
              Previously each step rendered the shared JobControl ActionButtons inside the body,
              which left them bottom-left inside a <table cellSpacing/cellPadding> with an empty
              leading cell for buttons this wizard never uses.

              Owning them here also removes a defect class rather than working around it:
              react-step-wizard injects its own `lastStep` into every child step via cloneElement,
              whose config wins over the element's own props — so a Finish handler passed down into
              a step was silently replaced by the wizard's "jump to the last step" navigator, which
              is a no-op on the final step. Nothing can intercept these handlers.
            */}
            <ModalFooter>
                {!jobStepDisabled && activeStep > 0 && (
                    <button
                        className="btn btn-primary"
                        onClick={() => wizard && wizard.previousStep()}
                        disabled={isSubmitting}
                    >
                        Previous
                    </button>
                )}
                {!onLastStep ? (
                    <button
                        className="btn btn-primary"
                        onClick={() => wizard && wizard.nextStep()}
                        disabled={!step1Ready}
                    >
                        Next
                    </button>
                ) : (
                    <button
                        className="btn btn-primary"
                        onClick={handleFinish}
                        disabled={!step2Ready || isSubmitting}
                    >
                        {isSubmitting ? "Saving…" : "Finish"}
                    </button>
                )}
                <button className="btn btn-secondary" onClick={handleClose} disabled={isSubmitting}>
                    Cancel
                </button>
            </ModalFooter>

            {/*
              C4 — cancelling this prompt returns to the duration step with the selection, duration
              and note all intact. It must NOT close the wizard: the customer has not been told their
              work would be discarded, and discarding it is the one thing they are not choosing here.
            */}
            <ConfirmOverwriteModal
                show={!!pendingOverwrites}
                errorId={knownError ? knownError.errorId : ""}
                overwrites={pendingOverwrites}
                newExpiry={resolveExpiry(duration)}
                busy={isSubmitting}
                onConfirm={submit}
                onClose={() => setPendingOverwrites(null)}
            />
        </Modal>
    );
}
